import React, { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog'
import { MapPin, Briefcase } from 'lucide-react'
import EventEndpoints from '../../../endpoints/EventEndpoints'
import { useEventContext } from '../EventContext'
import {
  SimpleConfigurationItem,
  Event as EventModel,
} from '../../../model/Event'
import { toast } from 'sonner'
import { ConfigItemList } from './ConfigItemList'

export type ScheduleConfigItemKind = 'location' | 'position'

const COPY: Record<
  ScheduleConfigItemKind,
  { title: string; description: string; label: string; icon: React.ReactNode }
> = {
  location: {
    title: 'Locations',
    description: 'Physical venues where shifts take place',
    label: 'Location',
    icon: <MapPin className="h-4 w-4" />,
  },
  position: {
    title: 'Positions',
    description: 'Job titles that can be assigned to shifts',
    label: 'Position',
    icon: <Briefcase className="h-4 w-4" />,
  },
}

interface ScheduleConfigItemsPanelProps {
  kind: ScheduleConfigItemKind
  /** Override the card heading, e.g. inside the wizard. */
  title?: string
  description?: string
}

/**
 * Self-contained locations-or-positions editor: the list, the endpoint calls
 * and the delete confirmation. The Schedule Config tab renders two of these
 * side by side; the setup wizard renders one per step. Keeping the write
 * semantics in one place matters — `add*` returns a ChildCreationResult whose
 * `parent` is the updated event, while `edit*`/`delete*` return the event
 * directly.
 */
export const ScheduleConfigItemsPanel: React.FC<ScheduleConfigItemsPanelProps> = ({
  kind,
  title,
  description,
}) => {
  const { event, setEvent, canEdit } = useEventContext()
  const [saving, setSaving] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<SimpleConfigurationItem | null>(
    null
  )

  const copy = COPY[kind]
  const items =
    (kind === 'location' ? event?.locations : event?.positions) || []

  const handleAdd = async (name: string) => {
    if (!event) return
    setSaving(true)
    try {
      const result =
        kind === 'location'
          ? await EventEndpoints.addEventLocation(event.id, { name })
          : await EventEndpoints.addEventPosition(event.id, { name })
      setEvent(result.parent)
      toast.success(`${copy.label} added`)
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        `Failed to add ${kind}`
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async (id: number, name: string) => {
    setSaving(true)
    try {
      const updatedEvent =
        kind === 'location'
          ? await EventEndpoints.editEventLocation(id, { field: 'name', value: name })
          : await EventEndpoints.editEventPosition(id, { field: 'name', value: name })
      setEvent(updatedEvent)
      toast.success(`${copy.label} updated`)
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        `Failed to update ${kind}`
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!itemToDelete) return
    const target = itemToDelete
    setItemToDelete(null)
    setSaving(true)
    try {
      const updatedEvent: EventModel =
        kind === 'location'
          ? await EventEndpoints.deleteEventLocation(target.id)
          : await EventEndpoints.deleteEventPosition(target.id)
      setEvent(updatedEvent)
      toast.success(`${copy.label} deleted`)
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message || error?.message || 'Failed to delete item'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <ConfigItemList
        title={title ?? copy.title}
        description={description ?? copy.description}
        icon={copy.icon}
        items={items}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={setItemToDelete}
        saving={saving}
        itemType={kind}
        canEdit={canEdit}
      />

      <AlertDialog
        open={itemToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !saving) setItemToDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {copy.label}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{itemToDelete?.name}"? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
